import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Calendar,
  FileText,
  Palette,
  CheckCircle2,
  Clock,
  Image as ImageIcon,
  Upload,
  Loader2,
  Maximize2,
  ChevronRight,
  ChevronLeft,
  ArrowLeft,
  Sparkles,
  Layers,
  MapPin,
  MessageSquare,
  Info,
  Receipt,
  User,
  Building2,
  Send,
} from "lucide-react";
import axiosInstance from "../../services/api/axios";
import { getCustomerSales } from "../../services/sales";
import { toast } from "react-toastify";
import { getMediaUrl, normalizePropertyConfigMediaForUI } from "../../utils/media";
import {
  getPresignedDownloadUrls,
  getPresignedUploadUrls,
  uploadFilesToS3,
  getPresignedDownloadUrl,
} from "../../utils/fileUtils";
import { normalizeMediaBySection, getSectionDisplayOrder } from "../../utils/designSubmissionMedia";
import { PrimaryButton, OutlineButton, CloseButton } from "../../components/Buttons";
import LoadingImage from "../../components/LoadingImage";
import LeadStatusTimeline from "../../components/InteriorDesigner/LeadStatusTimeline";
import CountdownBadge from "../../components/CountdownBadge";
import AvailabilityTag from "../../components/AvailabilityTag";
import { X } from "lucide-react";
import BoqReadOnlyView from "../BOQ/BoqReadOnlyView";
import DesignConversation from "../../components/interior/DesignConversation";
import DesignPhaseBadge from "../../components/interior/DesignPhaseBadge";
import { getDesignLead, reviewDesign } from "../../services/interiorDesign";
// Interior Execution moved to its own top-level section (/interior-execution),
// reached once the design is developer-approved — removed from Design Requests.

// Hash-routed pill tab IDs. Mirrors PhaseDetails.jsx pattern.
// "iterations" is intentionally omitted — the iteration grid was replaced by
// the master–detail aside, so #iterations now resolves to the overview parent.
const TAB_IDS = ["overview", "feedback", "boq"];
const parseTabFromHash = (hash) => {
  if (!hash) return "overview";
  const cleaned = hash.replace(/^#/, "").toLowerCase();
  return TAB_IDS.includes(cleaned) ? cleaned : "overview";
};

// Single source of truth for an iteration's lifecycle status, derived from
// BOTH the buyer decision (iterationStatus) AND the developer decision
// (developerApprovalStatus) — not just whether buyer feedback exists.
// Without this, an iteration that is buyer-approved AND developer-approved
// renders as "New / Awaiting feedback" because it carries no buyerFeedback,
// and the developer approval is never surfaced at all.
const getIterationStatusDisplay = (iter) => {
  const itStatus = (iter?.iterationStatus ?? iter?.iteration_status ?? "").toString().toUpperCase();
  const devStatus = (iter?.developerApprovalStatus ?? iter?.developer_approval_status ?? "").toString().toUpperCase();
  const hasFeedback = Boolean(
    (iter?.buyerFeedback ?? iter?.buyer_feedback) &&
      String(iter?.buyerFeedback ?? iter?.buyer_feedback).trim() !== ""
  );

  if (devStatus === "DEVELOPER_APPROVED") {
    return { key: "DEVELOPER_APPROVED", label: "Developer approved", tone: "emerald" };
  }
  if (devStatus === "DEVELOPER_REJECTED") {
    return { key: "DEVELOPER_REJECTED", label: "Developer rejected", tone: "rose" };
  }
  if (itStatus === "APPROVED") {
    // Buyer approved, waiting on the developer.
    return { key: "BUYER_APPROVED", label: "Buyer approved · developer pending", tone: "indigo" };
  }
  if (itStatus === "REJECTED") {
    return { key: "REJECTED", label: "Rejected", tone: "rose" };
  }
  if (itStatus === "FEEDBACK_PROVIDED" || hasFeedback) {
    return { key: "FEEDBACK", label: "Feedback sent", tone: "emerald" };
  }
  return { key: "AWAITING", label: "Awaiting feedback", tone: "amber" };
};

// Tailwind class clusters per tone so the pills stay visually consistent.
const STATUS_TONE_CLASSES = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  rose: "border-rose-200 bg-rose-50 text-rose-700",
};

const RequestDetails = () => {
  const { buyerId, interiorDesignerId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [loading, setLoading] = useState(true);
  const [saleData, setSaleData] = useState(null);
  const [, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [designer, setDesigner] = useState(null);
  const [configImages, setConfigImages] = useState([]);
  const [, setActiveTab] = useState("details");
  // Hash-routed pill tab state (overview/iterations/feedback/boq) — drives the new visual layout
  const [activeHashTab, setActiveHashTab] = useState(() =>
    typeof window !== "undefined" ? parseTabFromHash(window.location.hash) : "overview"
  );
  const [designSubmissions, setDesignSubmissions] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [selectedIterationId, setSelectedIterationId] = useState(null);
  const [selectedIterationData, setSelectedIterationData] = useState(null);
  const [selectedSubmissionData, setSelectedSubmissionData] = useState(null);

  // Request modification modal state (Buyer feedback update)
  const [showRequestModificationModal, setShowRequestModificationModal] = useState(false);
  const [modificationIterationId, setModificationIterationId] = useState(null);
  const [modificationIterationSnapshot, setModificationIterationSnapshot] = useState(null);
  const [modificationDescription, setModificationDescription] = useState("");
  const [modificationIsFinal, setModificationIsFinal] = useState(false);
  const [sendingModification, setSendingModification] = useState(false);
  // Decision modal state (buyer Approve / Reject)
  const [pendingDecision, setPendingDecision] = useState(null); // { type: 'APPROVE'|'REJECT', iterationId, iteration }
  const [decidingIteration, setDecidingIteration] = useState(false);
  // Buyer feedback media: uploaded items { url, type: "image", caption? }
  const [modificationFeedbackMedia, setModificationFeedbackMedia] = useState([]);
  const [uploadingFeedbackMedia, setUploadingFeedbackMedia] = useState(false);

  // Design advance payment (required before setting isFinal=true)
  const [leadPaymentsByLeadId, setLeadPaymentsByLeadId] = useState({});
  const [showDesignAdvanceModal, setShowDesignAdvanceModal] = useState(false);
  const [designAdvanceAmount, setDesignAdvanceAmount] = useState("5000");
  const [designAdvanceReference, setDesignAdvanceReference] = useState("");
  const [payingDesignAdvance, setPayingDesignAdvance] = useState(false);

  // Inbox sections: Design submission vs Interactions (same layout pattern as interior designer lead details)
  const [inboxView, setInboxView] = useState("design_submission");

  // Requirements modal (shows HTML-rendered requirements)
  const [showRequirementsModal, setShowRequirementsModal] = useState(false);
  const [notesModalContent, setNotesModalContent] = useState("");
  const [notesModalTitle, setNotesModalTitle] = useState("Designer Notes");

  // Carousel indices for config, portfolio, and design iteration images
  const [configCarouselIndex, setConfigCarouselIndex] = useState(0);
  const [portfolioCarouselIndex, setPortfolioCarouselIndex] = useState(0);
  const [designCarouselIndex, setDesignCarouselIndex] = useState(0);

  // Map of s3_key -> presigned URL for portfolio images (API returns s3_key, not url)
  const [portfolioImageUrls, setPortfolioImageUrls] = useState({});
  // Map of s3_key -> presigned URL for design iteration media (Design Images)
  const [designIterationImageUrls, setDesignIterationImageUrls] = useState({});

  // Simplified design conversation (derived thread + collapsed phase) and its clear actions.
  const [conversation, setConversation] = useState(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Check if data was passed via navigation state
        const passedData = routerLocation.state;
        let currentSaleData = null;
        let leadsArray = [];

        if (passedData?.saleData && passedData?.leads) {
          // Use passed data from Requests page
          currentSaleData = passedData.saleData;
          setSaleData(currentSaleData);

          leadsArray = Array.isArray(passedData.leads) ? passedData.leads : [];

          // Set designer info from passed data
          if (passedData.designer) {
            setDesigner(passedData.designer);
          }
        } else {
          // Fetch sale data if not passed
          if (buyerId) {
            const salesResponse = await getCustomerSales({
              customerSaleId: parseInt(buyerId),
            });

            if (salesResponse && Array.isArray(salesResponse) && salesResponse.length > 0) {
              currentSaleData = salesResponse[0];
              setSaleData(currentSaleData);
            }
          }

          // Fetch leads for this property, user, and interior designer
          const propertyId = currentSaleData?.propertyData?.propertyId;
          const userId = currentSaleData?.user?.userId;

          if (propertyId && userId && interiorDesignerId) {
            const response = await axiosInstance.get("/interior-designers/leads", {
              params: {
                propertyId: propertyId,
                userId: userId,
                interiorDesignerId: parseInt(interiorDesignerId),
                page: 1,
                page_size: 1000,
              },
            });

            if (Array.isArray(response)) {
              leadsArray = response;
            } else if (Array.isArray(response?.items)) {
              leadsArray = response.items;
            } else if (Array.isArray(response?.data?.items)) {
              leadsArray = response.data.items;
            } else if (Array.isArray(response?.data)) {
              leadsArray = response.data;
            }

            // Extract designer info from first lead
            if (leadsArray.length > 0) {
              const firstLead = leadsArray[0];
              const designerInfo = firstLead.interiorDesignerPortfolioItems?.[0]?.interiorDesigner;
              if (designerInfo) {
                setDesigner({
                  interiorDesignerId: designerInfo.interiorDesignerId,
                  name: designerInfo.name,
                  email: designerInfo.email,
                  mobile: designerInfo.mobile,
                });
              }
            }
          }
        }

        // Sort leads by created date (newest first)
        leadsArray.sort((a, b) => {
          const dateA = new Date(a.createdDate || 0);
          const dateB = new Date(b.createdDate || 0);
          return dateB - dateA;
        });

        setLeads(leadsArray);

        // Set first lead as selected by default (this is the lead, not a request)
        if (leadsArray.length > 0) {
          setSelectedLead(leadsArray[0]);
          // Fetch design submissions for the selected lead
          fetchDesignSubmissions(leadsArray[0].interiorDesignerLeadId);
        }

        // Fetch config images
        const propertyConfigId = currentSaleData?.propertyData?.propertyConfig?.propertyConfigId;
        if (propertyConfigId) {
          try {
            const imagesResponse = await axiosInstance.get("/property/config/media", {
              params: {
                property_config_id: propertyConfigId,
              },
            });

            const { gallery } = normalizePropertyConfigMediaForUI(imagesResponse);
            const imageOnly = gallery.filter((m) =>
              (m.contentType || "").toLowerCase().startsWith("image/")
            );
            setConfigImages(imageOnly);
          } catch (error) {
            console.error("Error fetching config images:", error);
            setConfigImages([]);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch request details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [buyerId, interiorDesignerId, routerLocation.state]);

  // Fetch design submissions when selected lead changes
  useEffect(() => {
    if (selectedLead?.interiorDesignerLeadId) {
      fetchDesignSubmissions(selectedLead.interiorDesignerLeadId);
    }
  }, [selectedLead?.interiorDesignerLeadId]);

  // Keep activeHashTab in sync with the URL hash (back/forward, deep links)
  useEffect(() => {
    setActiveHashTab(parseTabFromHash(routerLocation.hash));
  }, [routerLocation.hash]);

  const selectHashTab = (tabId) => {
    setActiveHashTab(tabId);
    if (typeof window !== "undefined") {
      navigate(`${routerLocation.pathname}#${tabId}`, { replace: true });
    }
  };

  const fetchDesignSubmissions = async (leadId) => {
    if (!leadId) return;
    try {
      setLoadingDesigns(true);
      const lead = await getDesignLead(leadId);
      const deliverables = lead?.deliverables || [];
      // Adapt the flat lead + deliverables[] to the legacy {designSubmission, designIterations}
      // wrapper shape (one pseudo-submission per lead) — the SAME shape every consumer below
      // reads (submissionWrapper.designSubmission / .designIterations). `iterationStatus` is
      // aliased from the deliverable's single `status` so the legacy grid's derivation keeps
      // working. (Mirrors the ID-side adapter in LeadRequestDetails.jsx.)
      const adapted = deliverables.length
        ? [
            {
              designSubmission: {
                designSubmissionId: leadId,
                title: lead.title,
                description: lead.description,
                createdDate: lead.createdDate,
                idLead: lead,
              },
              designIterations: deliverables.map((d) => ({ ...d, iterationStatus: d.status })),
            },
          ]
        : [];
      setDesignSubmissions(adapted);
    } catch (error) {
      console.error("Error fetching lead detail:", error);
      setDesignSubmissions([]);
    } finally {
      setLoadingDesigns(false);
    }
  };

  // Load the flat lead detail (single status + deliverables[]) for the selected lead.
  const fetchConversation = async (leadId) => {
    if (!leadId) return;
    setLoadingConversation(true);
    try {
      const data = await getDesignLead(leadId);
      setConversation(data);
    } catch (error) {
      setConversation(null);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Keep the thread in sync: reload it on lead change AND whenever legacy actions refresh
  // the submissions (so the simplified view never drifts from the master-detail grid).
  useEffect(() => {
    const leadId = selectedLead?.interiorDesignerLeadId;
    if (leadId) fetchConversation(leadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLead?.interiorDesignerLeadId, designSubmissions]);

  // Clear reviewer action: approve/finalize the latest (final) design.
  const handleApproveDesign = async () => {
    const leadId = selectedLead?.interiorDesignerLeadId;
    if (!leadId) return;
    if (!window.confirm("Approve this design? Execution will be prepared from it.")) return;
    setSubmittingAction(true);
    try {
      await reviewDesign(leadId, "APPROVE");
      toast.success("Design approved.");
      await Promise.all([fetchConversation(leadId), fetchDesignSubmissions(leadId)]);
    } catch (error) {
      toast.error(error?.detail || error?.response?.data?.detail || "Could not approve the design.");
    } finally {
      setSubmittingAction(false);
    }
  };

  // Re-fetch the leads for this property/buyer/designer and refresh the
  // currently-selected lead in place (preserving the selection). The lead
  // object carries status / hasNewRequest / featured / deadlines, none of
  // which `fetchDesignSubmissions` refreshes — so without this the
  // lead-level chips and badges go stale after an action.
  const refreshLeads = async () => {
    const propertyId = saleData?.propertyData?.propertyId;
    const userId = saleData?.user?.userId;
    if (!propertyId || !userId || !interiorDesignerId) return;
    try {
      const response = await axiosInstance.get("/interior-designers/leads", {
        params: {
          propertyId,
          userId,
          interiorDesignerId: parseInt(interiorDesignerId),
          page: 1,
          page_size: 1000,
        },
      });
      let leadsArray = [];
      if (Array.isArray(response)) leadsArray = response;
      else if (Array.isArray(response?.items)) leadsArray = response.items;
      else if (Array.isArray(response?.data?.items)) leadsArray = response.data.items;
      else if (Array.isArray(response?.data)) leadsArray = response.data;

      leadsArray.sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0));
      setLeads(leadsArray);
      setSelectedLead((prev) => {
        if (!prev) return leadsArray[0] ?? null;
        const match = leadsArray.find(
          (l) => l.interiorDesignerLeadId === prev.interiorDesignerLeadId
        );
        return match ?? prev;
      });
    } catch (error) {
      console.error("Error refreshing leads:", error);
    }
  };

  // Flatten iterations from all submissions to create the requests list
  // Each request represents an iteration
  const requests = designSubmissions
    .flatMap((submissionWrapper) => {
      const submission = submissionWrapper.designSubmission || submissionWrapper;
      const iterations = submissionWrapper.designIterations || [];

      return iterations.map((iteration) => ({
        id: iteration.designIterationId || iteration.id,
        iterationNumber: iteration.iterationNumber || 1,
        title: submission.title || "Design Submission",
        description: submission.description || "",
        createdDate: iteration.createdDate || submission.createdDate,
        submission: submission,
        iteration: iteration,
      }));
    })
    .sort((a, b) => {
      const ta = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
      const tb = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
      const safeA = Number.isFinite(ta) ? ta : 0;
      const safeB = Number.isFinite(tb) ? tb : 0;
      if (safeB !== safeA) return safeB - safeA;
      return (b?.iterationNumber || 0) - (a?.iterationNumber || 0);
    });

  // Latest iteration id per submission. Buyer lifecycle actions (approve /
  // reject / request modification) are only valid on the latest iteration of a
  // submission; once the designer uploads a newer one the older becomes
  // read-only. Mirrors the backend supersession gate (409).
  const latestIterationIds = (() => {
    const ids = new Set();
    (designSubmissions || []).forEach((sw) => {
      const iterations = sw.designIterations || [];
      let best = null;
      iterations.forEach((it) => {
        const n = it.iterationNumber || 0;
        if (best === null || n > (best.iterationNumber || 0)) best = it;
      });
      if (best) ids.add(best.designIterationId || best.id);
    });
    return ids;
  })();

  // Get the selected iteration and its parent submission
  const selectedIteration = selectedIterationData ||
    (selectedIterationId ? requests.find(req => req.id === selectedIterationId)?.iteration : null);

  const selectedSubmission = selectedSubmissionData ||
    (selectedIteration ? requests.find(req => req.id === selectedIterationId)?.submission : null);

  const selectedIterationName =
    selectedIteration?.interactionName?.trim?.() ||
    selectedIteration?.interaction_name?.trim?.() ||
    selectedIteration?.iterationName?.trim?.() ||
    selectedIteration?.iteration_name?.trim?.() ||
    selectedIteration?.title?.trim?.() ||
    (selectedIteration?.iterationNumber ? `Iteration #${selectedIteration.iterationNumber}` : "");

  // Whether buyer has already sent feedback for the currently selected iteration
  const hasBuyerFeedbackForSelectedIteration =
    Boolean(selectedIteration && selectedIteration.buyerFeedback && selectedIteration.buyerFeedback.trim() !== "");

  // Whether the selected iteration is the latest of its submission (only the
  // latest is actionable — see latestIterationIds above).
  const isSelectedIterationLatest =
    selectedIterationId != null && latestIterationIds.has(selectedIterationId);

  // Auto-select latest iteration by default; show Design submission when no iterations exist
  useEffect(() => {
    if (!loadingDesigns) {
      const flat = designSubmissions.flatMap((submissionWrapper) => {
        const submission = submissionWrapper.designSubmission || submissionWrapper;
        const iterations = submissionWrapper.designIterations || [];
        return iterations.map((iteration) => ({
          id: iteration.designIterationId || iteration.id,
          iterationNumber: iteration.iterationNumber || 1,
          title: submission.title || "Design Submission",
          description: submission.description || "",
          createdDate: iteration.createdDate || submission.createdDate,
          submission: submission,
          iteration: iteration,
        }));
      });
      const sorted = flat.sort((a, b) => {
        const ta = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
        const tb = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
        const safeA = Number.isFinite(ta) ? ta : 0;
        const safeB = Number.isFinite(tb) ? tb : 0;
        if (safeB !== safeA) return safeB - safeA;
        return (b?.iterationNumber || 0) - (a?.iterationNumber || 0);
      });
      const latestRequest = sorted[0];
      if (latestRequest) {
        setSelectedIterationId(latestRequest.id);
        setSelectedIterationData(latestRequest.iteration);
        setSelectedSubmissionData(latestRequest.submission);
        setInboxView("interactions");
      } else {
        setInboxView("design_submission");
      }
    }
  }, [designSubmissions, loadingDesigns]);

  // Reset carousel indices when lead changes
  useEffect(() => {
    setConfigCarouselIndex(0);
    setPortfolioCarouselIndex(0);
    setDesignCarouselIndex(0);
  }, [selectedLead?.interiorDesignerLeadId, selectedIterationId, inboxView]);

  // Fetch presigned URLs for design iteration media (Design Images in each iteration)
  useEffect(() => {
    const fetchDesignIterationImageUrls = async () => {
      const s3Keys = [];
      (designSubmissions || []).forEach((submissionWrapper) => {
        const iterations = submissionWrapper?.designIterations || [];
        iterations.forEach((iteration) => {
          const media = iteration?.media;
          if (!media) return;
          const bySection = normalizeMediaBySection(media);
          Object.values(bySection || {}).flat().forEach((item) => {
            const key = typeof item === "string"
              ? item
              : (item?.s3_key || item?.s3Key || item?.key || item?.media_key || item?.mediaKey || item?.file_key);
            if (!key || typeof key !== "string" || (item && typeof item === "object" && (item?.url || item?.downloadUrl))) return;
            if (key.startsWith("/9j/") || key.startsWith("iVBORw0KGgo") || key.startsWith("data:")) return;
            s3Keys.push(key);
          });
        });
      });
      const uniqueS3Keys = [...new Set(s3Keys)];
      if (uniqueS3Keys.length === 0) {
        setDesignIterationImageUrls({});
        return;
      }
      try {
        const presignedUrls = await getPresignedDownloadUrls(uniqueS3Keys, axiosInstance);
        const urlMap = {};
        presignedUrls.forEach((presigned, index) => {
          const s3Key = uniqueS3Keys[index];
          const downloadUrl = presigned.download_url || presigned.downloadUrl || presigned.url;
          if (downloadUrl) urlMap[s3Key] = downloadUrl;
        });
        setDesignIterationImageUrls(urlMap);
      } catch (error) {
        console.error("Error fetching presigned URLs for design iteration images:", error);
        setDesignIterationImageUrls({});
      }
    };
    if (designSubmissions?.length) {
      fetchDesignIterationImageUrls();
    } else {
      setDesignIterationImageUrls({});
    }
  }, [designSubmissions]);

  // Fetch presigned URLs for portfolio images with s3_key (API returns S3 keys, not direct URLs)
  useEffect(() => {
    const fetchPortfolioImageUrls = async () => {
      const portfolioItems = selectedLead?.interiorDesignerPortfolioItems || [];
      const imagesWithS3Key = [];
      portfolioItems.forEach((item) => {
        const imgs = item.images ? (Array.isArray(item.images) ? item.images : Object.values(item.images || {})) : [];
        imgs.forEach((img) => {
          const s3Key = img?.s3_key || img?.s3Key || img?.key;
          if (img && typeof img === "object" && s3Key && !img.url && !img.downloadUrl) {
            imagesWithS3Key.push(img);
          }
        });
      });
      if (imagesWithS3Key.length === 0) {
        setPortfolioImageUrls({});
        return;
      }
      try {
        const s3Keys = imagesWithS3Key.map((img) => img.s3_key || img.s3Key || img.key);
        const uniqueS3Keys = [...new Set(s3Keys)];
        const presignedUrls = await getPresignedDownloadUrls(uniqueS3Keys, axiosInstance);
        const urlMap = {};
        presignedUrls.forEach((presigned, index) => {
          const s3Key = uniqueS3Keys[index];
          const downloadUrl = presigned.download_url || presigned.downloadUrl || presigned.url;
          if (downloadUrl) urlMap[s3Key] = downloadUrl;
        });
        setPortfolioImageUrls(urlMap);
      } catch (error) {
        console.error("Error fetching presigned URLs for portfolio images:", error);
        setPortfolioImageUrls({});
      }
    };
    if (selectedLead && !loading) {
      fetchPortfolioImageUrls();
    }
  }, [selectedLead, loading]);

  const handleBack = () => {
    navigate(`/bv-user/buyer/${buyerId}/details/#requests`);
  };

  // Helper function to get image source
  const getImageSrc = (image) => {
    if (!image) return null;

    // Portfolio / design iteration images: s3_key/s3Key/key/media_key/mediaKey - use presigned URL if fetched
    const s3Key = image?.s3_key || image?.s3Key || image?.key || image?.media_key || image?.mediaKey || image?.file_key;
    if (typeof image === "object" && s3Key) {
      if (portfolioImageUrls[s3Key]) return portfolioImageUrls[s3Key];
      if (designIterationImageUrls[s3Key]) return designIterationImageUrls[s3Key];
      if (image.url || image.downloadUrl) return getMediaUrl(image) || image.url || image.downloadUrl;
      // Legacy: key may be base64 image data (comma-separated media string from older submissions)
      if (s3Key.startsWith("/9j/") || s3Key.startsWith("iVBORw0KGgo")) {
        const prefix = s3Key.startsWith("/9j/") ? "data:image/jpeg;base64," : "data:image/png;base64,";
        return prefix + s3Key;
      }
    }

    // content + contentType (getMediaUrl format)
    if (typeof image === "object" && image.content && image.contentType) {
      return getMediaUrl(image);
    }

    if (typeof image === "object" && image.base64) {
      const base64String = image.base64;
      if (base64String.startsWith("/9j/")) {
        return `data:image/jpeg;base64,${base64String}`;
      } else if (base64String.startsWith("iVBORw0KGgo")) {
        return `data:image/png;base64,${base64String}`;
      } else if (base64String.startsWith("data:")) {
        return base64String;
      } else {
        return `data:image/jpeg;base64,${base64String}`;
      }
    }

    if (typeof image === "string") {
      if (image.startsWith("http://") || image.startsWith("https://") || image.startsWith("data:")) {
        return image;
      }
      if (image.startsWith("data:image/") || image.startsWith("/9j/") || image.startsWith("iVBORw0KGgo") || image.length > 100) {
        if (image.startsWith("/9j/")) {
          return `data:image/jpeg;base64,${image}`;
        } else if (image.startsWith("iVBORw0KGgo")) {
          return `data:image/png;base64,${image}`;
        } else if (image.startsWith("data:")) {
          return image;
        } else {
          return `data:image/jpeg;base64,${image}`;
        }
      }
    }
    return null;
  };

  const getImageUrl = (image) => getImageSrc(image) || getMediaUrl(image);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const buildIterationUpdatePayload = (iteration, overrides) => {
    const payload = {};
    const keysToPreserve = [
      "estimatedPrice",
      "media",
      "designerNotes",
      "buyerFeedback",
      "isFinal",
      "isIdAcknowledged",
      "iterationStatus",
      "developerApprovalStatus",
      "developerApprovalNotes",
    ];

    keysToPreserve.forEach((key) => {
      if (iteration && iteration[key] !== undefined) {
        payload[key] = iteration[key];
      }
    });

    return { ...payload, ...overrides };
  };

  const fetchLeadPaymentStatus = async (leadId) => {
    if (!leadId) return;
    try {
      const response = await axiosInstance.get(`/interior-designers/id-lead-payments/${parseInt(leadId)}`);

      let payments = [];
      if (Array.isArray(response)) {
        payments = response;
      } else if (Array.isArray(response?.data)) {
        payments = response.data;
      } else if (Array.isArray(response?.items)) {
        payments = response.items;
      }

      const advancePayment = payments.find((p) =>
        (p.paymentType || p.payment_type) === "DESIGN_ADVANCE"
      );
      if (advancePayment) {
        setLeadPaymentsByLeadId((prev) => ({
          ...prev,
          [parseInt(leadId)]: {
            paid: true,
            amount: advancePayment.paymentAmount ?? advancePayment.payment_amount,
            reference: advancePayment.paymentReference ?? advancePayment.payment_reference,
            date: advancePayment.paymentDate ?? advancePayment.payment_date,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching payment status:", error);
    }
  };

  const openRequestModification = (request) => {
    const iterationId = request?.id;
    if (!iterationId) {
      toast.error("Iteration ID not found");
      return;
    }

    const leadId =
      request?.submission?.idLeadId ||
      request?.iteration?.idLeadId ||
      selectedLead?.interiorDesignerLeadId ||
      null;

    if (leadId) {
      fetchLeadPaymentStatus(leadId);
    }

    setSelectedIterationId(iterationId);
    setSelectedIterationData(request.iteration);
    setSelectedSubmissionData(request.submission);
    setActiveTab("designs");

    setModificationIterationId(iterationId);
    setModificationIterationSnapshot(request.iteration || null);
    setModificationDescription(request?.iteration?.buyerFeedback || "");
    setModificationIsFinal(Boolean(request?.iteration?.isFinal));
    setShowDesignAdvanceModal(false);
    setDesignAdvanceAmount("5000");
    setDesignAdvanceReference("");
    setShowRequestModificationModal(true);
  };

  const closeRequestModification = () => {
    if (sendingModification || payingDesignAdvance) return;

    setShowRequestModificationModal(false);
    setModificationIterationId(null);
    setModificationIterationSnapshot(null);
    setModificationDescription("");
    setModificationIsFinal(false);
    setModificationFeedbackMedia([]);
    setShowDesignAdvanceModal(false);
    setDesignAdvanceAmount("5000");
    setDesignAdvanceReference("");
  };

  // Upload selected image files to S3 and add to modificationFeedbackMedia with presigned download URLs
  const handleFeedbackMediaSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      toast.error("Please select image files only.");
      return;
    }
    setUploadingFeedbackMedia(true);
    try {
      // BE-side folder must match USER_ALLOWED_FOLDER_TYPES /
      // CUSTOMER_ALLOWED_FOLDER_TYPES (services/shared/.../schemas/media.py),
      // which canonicalises this bucket as "feedback". The BE namespaces it
      // automatically to users/{id}/feedback/... or customers/{id}/feedback/...
      // depending on the caller's entity type.
      const presignedData = await getPresignedUploadUrls(
        imageFiles,
        "feedback",
        axiosInstance
      );
      const uploadData = presignedData.map((presigned, index) => ({
        file: imageFiles[index],
        upload_url: presigned.upload_url,
        content_type: presigned.content_type || imageFiles[index].type,
      }));
      await uploadFilesToS3(uploadData);
      const urls = [];
      for (let i = 0; i < presignedData.length; i++) {
        const s3Key = presignedData[i].s3_key;
        const result = await getPresignedDownloadUrl(s3Key, axiosInstance);
        const url = result.download_url || result.downloadUrl;
        // Keep `url` for the modal's local preview, but persist `s3_key` too — the backend
        // stores the key (+ bucket) and re-presigns a fresh URL on every read, so the image
        // never breaks (a stored presigned URL would expire within minutes).
        if (url || s3Key) urls.push({ s3_key: s3Key, url, type: "image", caption: "" });
      }
      setModificationFeedbackMedia((prev) => [...prev, ...urls]);
    } catch (err) {
      console.error("Error uploading feedback media:", err);
      toast.error(err?.response?.data?.message || "Failed to upload images.");
    } finally {
      setUploadingFeedbackMedia(false);
      e.target.value = "";
    }
  };

  const updateFeedbackMediaCaption = (index, caption) => {
    setModificationFeedbackMedia((prev) => {
      const next = [...prev];
      if (next[index]) next[index] = { ...next[index], caption: caption || "" };
      return next;
    });
  };

  const removeFeedbackMedia = (index) => {
    setModificationFeedbackMedia((prev) => prev.filter((_, i) => i !== index));
  };

  const currentLeadId =
    selectedLead?.interiorDesignerLeadId ||
    selectedLead?.idLeadId ||
    selectedSubmissionData?.idLeadId ||
    null;

  const isDesignAdvancePaid = Boolean(
    currentLeadId && leadPaymentsByLeadId?.[parseInt(currentLeadId)]?.paid
  );

  const submitDesignAdvancePayment = async () => {
    if (!currentLeadId) {
      toast.error("Lead ID not found");
      return false;
    }

    const amountNum = parseFloat(designAdvanceAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      toast.error("Please enter a valid payment amount");
      return false;
    }
    if (!designAdvanceReference.trim()) {
      toast.error("Please enter a payment reference");
      return false;
    }

    try {
      setPayingDesignAdvance(true);
      const payload = [
        {
          id_lead_id: parseInt(currentLeadId),
          payment_type: "DESIGN_ADVANCE",
          payment_amount: amountNum,
          payment_date: new Date().toISOString().slice(0, -1),
          payment_reference: designAdvanceReference.trim(),
        },
      ];

      await axiosInstance.post("/interior-designers/id-lead-payments", payload, {
        headers: { "Content-Type": "application/json" },
      });

      setLeadPaymentsByLeadId((prev) => ({
        ...prev,
        [parseInt(currentLeadId)]: {
          paid: true,
          amount: amountNum,
          reference: designAdvanceReference.trim(),
          date: payload[0].payment_date,
        },
      }));

      toast.success("Design advance payment recorded");
      setShowDesignAdvanceModal(false);
      // Auto-refresh lead + submissions so the new payment state is reflected.
      if (selectedLead?.interiorDesignerLeadId) {
        await Promise.all([
          fetchDesignSubmissions(selectedLead.interiorDesignerLeadId),
          refreshLeads(),
        ]);
      }
      return true;
    } catch (error) {
      console.error("Error recording design advance payment:", error);
      toast.error(error?.response?.data?.message || "Failed to record payment");
      return false;
    } finally {
      setPayingDesignAdvance(false);
    }
  };

  const submitRequestModification = async (alreadyPaid = false) => {
    if (!modificationIterationId) {
      toast.error("Iteration ID not found");
      return;
    }
    if (!modificationDescription.trim()) {
      toast.error("Please add a description");
      return;
    }
    if (modificationIsFinal && !isDesignAdvancePaid && !alreadyPaid) {
      setShowDesignAdvanceModal(true);
      toast.error("Design advance payment is required for final iteration");
      return;
    }

    try {
      setSendingModification(true);

      const baseIteration = modificationIterationSnapshot || selectedIteration || {};
      const buyerFeedbackMediaPayload =
        modificationFeedbackMedia.length > 0
          ? modificationFeedbackMedia.map((item) => ({
            // Persist the storage key (re-presigned on read); fall back to a raw url only
            // for any legacy item that has no key.
            ...(item.s3_key ? { s3_key: item.s3_key } : { url: item.url }),
            type: item.type || "image",
            ...(item.caption ? { caption: item.caption } : {}),
          }))
          : undefined;
      // Request changes on the lead's latest deliverable (single review state-machine).
      await reviewDesign(
        selectedLead.interiorDesignerLeadId,
        "REQUEST_CHANGES",
        modificationDescription.trim(),
        buyerFeedbackMediaPayload || null
      );

      toast.success("Request sent successfully");
      setShowRequestModificationModal(false);
      setModificationIterationId(null);
      setModificationIterationSnapshot(null);
      setModificationDescription("");
      setModificationIsFinal(false);
      setModificationFeedbackMedia([]);

      // Clear cached selection objects so refreshed data is shown
      setSelectedIterationData(null);
      setSelectedSubmissionData(null);

      if (selectedLead?.interiorDesignerLeadId) {
        await Promise.all([
          fetchDesignSubmissions(selectedLead.interiorDesignerLeadId),
          refreshLeads(),
        ]);
      }
    } catch (error) {
      console.error("Error updating design iteration:", error);
      toast.error(error?.response?.data?.message || "Failed to send request");
    } finally {
      setSendingModification(false);
    }
  };

  // Buyer Approve / Reject: PUT iterationStatus=APPROVED|REJECTED on the
  // selected iteration, then refresh. Confirmation-gated via pendingDecision.
  const submitDecision = async () => {
    if (!pendingDecision) return;
    const { type } = pendingDecision;
    setDecidingIteration(true);
    try {
      // Approve → APPROVE; the legacy "reject" maps to a change-request (no reject state now).
      if (type === "APPROVE") {
        await reviewDesign(selectedLead.interiorDesignerLeadId, "APPROVE");
      } else {
        await reviewDesign(
          selectedLead.interiorDesignerLeadId,
          "REQUEST_CHANGES",
          "Please revise this design."
        );
      }
      toast.success(
        type === "APPROVE" ? "Design approved." : "Change request sent."
      );
      setPendingDecision(null);
      setSelectedIterationData(null);
      setSelectedSubmissionData(null);
      if (selectedLead?.interiorDesignerLeadId) {
        await Promise.all([
          fetchDesignSubmissions(selectedLead.interiorDesignerLeadId),
          refreshLeads(),
        ]);
      }
    } catch (error) {
      console.error("Error submitting decision:", error);
      toast.error(error?.response?.data?.message || "Failed to submit decision");
    } finally {
      setDecidingIteration(false);
    }
  };

  // Skeleton Loader — slate visual language
  const SkeletonLoader = () => (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-slate-100" />
      <div className="h-44 animate-pulse rounded-2xl bg-slate-100" />
      <div className="h-12 w-80 animate-pulse rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
      <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!saleData) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <FileText className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">Buyer data not found</p>
          <p className="mt-1 text-sm text-slate-500">We couldn't load this design request.</p>
          <OutlineButton onClick={handleBack} className="mt-4">
            Back to buyer details
          </OutlineButton>
        </div>
      </div>
    );
  }

  const customer = saleData.user || {};
  const property = saleData.propertyData || {};
  const project = property?.projectPhase?.project || {};
  const config = saleData?.propertyConfig || {};
  const address = project?.address || {};

  const customerName = `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || "N/A";
  const unitNo = property.propertyNo || "N/A";
  const location = address.city && address.state
    ? `${project.projectName || ""}, ${address.city}, ${address.state}`.trim()
    : project.projectName || "N/A";

  // Get configuration type and area
  const configType = config.sizeBhk || config.configName || "N/A";
  const area = config.carpetAreaSqft
    ? `${config.carpetAreaSqft} sq.ft`
    : config.carpetAreaSqmt
      ? `${config.carpetAreaSqmt} sq.mt`
      : "N/A";

  // Get tower and floor info
  const towerAndFloor = property.buildingNo
    ? `Tower ${property.buildingNo}${property.floorNo ? `, ${property.floorNo} Floor` : ""}`
    : property.floorNo
      ? `${property.floorNo} Floor`
      : "N/A";

  const featuredIterations = selectedLead?.featuredDesignIterations || selectedLead?.featured_design_iterations || [];
  const featuredDesign = featuredIterations[0];
  const featuredIteration = featuredDesign?.designIteration || featuredDesign?.design_iteration || null;
  const isFeaturedRequest = Boolean(featuredIterations.length && featuredIteration);
  const featuredMediaBySection = featuredIteration?.media ? normalizeMediaBySection(featuredIteration.media) : {};
  const featuredDesignItems = isFeaturedRequest
    ? Object.entries(featuredMediaBySection).flatMap(([section, items]) =>
      (Array.isArray(items) ? items : []).map((item, idx) =>
        (typeof item === "string" ? { key: item, section, idx } : { ...item, section, idx })
      )
    )
    : [];
  const portfolioItems = selectedLead?.interiorDesignerPortfolioItems || [];
  const portfolioImages = portfolioItems.flatMap((item) => {
    const imgs = item.images ? (Array.isArray(item.images) ? item.images : Object.values(item.images || {})) : [];
    return imgs.map((img, idx) => ({ img, title: item.title, idx }));
  });
  const designReferenceImages = isFeaturedRequest
    ? featuredDesignItems.map((item, idx) => ({
      img: item,
      title: item?.section || "Design",
      idx,
    }))
    : portfolioImages;
  const portfolioTitle = isFeaturedRequest
    ? featuredIteration?.interactionName
    || featuredIteration?.interaction_name
    || featuredIteration?.iterationName
    || featuredIteration?.iteration_name
    || featuredIteration?.title
    || featuredIteration?.designSubmission?.title
    || featuredIteration?.design_submission?.title
    || (featuredIteration?.iterationNumber ? `Design Iteration #${featuredIteration.iterationNumber}` : "Featured Design")
    : (selectedLead?.interiorDesignerPortfolioItems?.[0]?.title || "Interior Design Request");
  const designReferenceLabel = isFeaturedRequest ? "Design & related items" : "Portfolio";
  const firstSubmissionWrapper = designSubmissions[0];
  const designSubmission = firstSubmissionWrapper?.designSubmission || firstSubmissionWrapper || null;

  const openNotesModal = (content, title = "Designer Notes") => {
    if (!content || !String(content).trim()) return;
    setNotesModalTitle(title);
    setNotesModalContent(String(content));
  };

  const breadcrumbDesignerName =
    designer?.name ||
    selectedLead?.interiorDesignerPortfolioItems?.[0]?.interiorDesigner?.name ||
    "Design request";

  // Derived stat tiles + tab metadata for the slate-style layout
  const iterationCount = requests.length;
  const finalIterationCount = requests.filter(
    (r) => Boolean(r?.iteration?.isFinal ?? r?.iteration?.is_final)
  ).length;
  const lastIterationDate = requests[0]?.createdDate || designSubmission?.createdDate || null;
  const daysSinceLastUpdate = (() => {
    if (!lastIterationDate) return null;
    const d = new Date(lastIterationDate);
    if (Number.isNaN(d.getTime())) return null;
    const diffMs = Date.now() - d.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  })();
  const ackStatusLabel = selectedLead?.isIdAcknowledged ? "Acknowledged" : "Pending";
  const designerName = breadcrumbDesignerName;

  const overviewStatTiles = [
    { label: "Iterations", value: iterationCount, icon: Layers },
    { label: "Final marked", value: finalIterationCount, icon: CheckCircle2 },
    {
      label: "Last update",
      value:
        daysSinceLastUpdate == null
          ? "—"
          : daysSinceLastUpdate === 0
            ? "Today"
            : `${daysSinceLastUpdate}d ago`,
      icon: Clock,
    },
    { label: "Acknowledged", value: ackStatusLabel, icon: Info },
  ];

  // When user clicks an iteration card, jump to the feedback tab for that iteration
  const handleSelectIteration = (request) => {
    setInboxView("interactions");
    setSelectedIterationId(request.id);
    setSelectedIterationData(request.iteration);
    setSelectedSubmissionData(request.submission);
    selectHashTab("feedback");
  };

  return (
    <div className="px-4 sm:px-6 py-6 space-y-6 antialiased">
      {/* Back link above the hero (mirrors PhaseDetails) */}
      <button
        type="button"
        onClick={handleBack}
        className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-flamingo-600"
      >
        <ArrowLeft size={14} />
        Back to requests
      </button>

      {/* Slate gradient hero band */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/30 via-slate-800/20 to-transparent" />

        <div className="relative px-6 py-7 sm:px-8 sm:py-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
            <Sparkles size={12} />
            Buildvizer User · Design Request
          </div>

          <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">
            {(selectedIterationName && inboxView === "interactions"
              ? selectedIterationName
              : portfolioTitle) || "Design Request"}
          </h1>

          <p className="mt-1 text-sm text-white/80">
            <span className="inline-flex items-center gap-1">
              <User size={12} className="opacity-80" />
              {customerName}
            </span>
            <span className="text-white/50"> · </span>
            <span className="inline-flex items-center gap-1">
              <Palette size={12} className="opacity-80" />
              {designerName}
            </span>
            {project.projectName ? (
              <>
                <span className="text-white/50"> · </span>
                <span className="inline-flex items-center gap-1">
                  <Building2 size={12} className="opacity-80" />
                  {project.projectName}
                  {unitNo !== "N/A" ? ` · Unit ${unitNo}` : ""}
                </span>
              </>
            ) : null}
          </p>

          {location && location !== "N/A" && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-white/70">
              <MapPin size={13} className="mt-0.5 shrink-0" />
              <span className="line-clamp-2">{location}</span>
            </div>
          )}

          {/* Status pills row */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {selectedLead?.isIdAcknowledged ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                <CheckCircle2 size={12} />
                Acknowledged
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                <Clock size={12} />
                Pending acknowledgement
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
              <Layers size={12} />
              {iterationCount} {iterationCount === 1 ? "iteration" : "iterations"}
            </span>
            {lastIterationDate && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                <Clock size={12} />
                Last update {formatDate(lastIterationDate)}
              </span>
            )}
            {finalIterationCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                <CheckCircle2 size={12} />
                {finalIterationCount} marked final
              </span>
            )}
            {/* Designer-facing deadline countdowns (render only while a deadline exists) */}
            <CountdownBadge
              deadline={selectedLead?.acknowledgementDeadline}
              label="Designer acknowledges in"
              expiredLabel="Acknowledgement"
            />
            <CountdownBadge
              deadline={selectedLead?.designDeliveryDeadline}
              label="Design delivery in"
              expiredLabel="Design delivery"
            />
          </div>

          {/* Material budget the buyer set for this request (BV sees rupees) */}
          {selectedLead?.materialBudget != null && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white/85 ring-1 ring-white/10 backdrop-blur-sm">
              <span className="font-medium">Material budget</span>
              <span className="font-semibold tabular-nums">
                ₹{Number(selectedLead.materialBudget).toLocaleString("en-IN")}
              </span>
              {Number(selectedLead?.budgetBufferPercent) > 0 && (
                <span className="text-white/70">· buffer {Number(selectedLead.budgetBufferPercent)}%</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Master–detail: the base Design request is the parent; every design
          iteration is stacked below it (latest first). Clicking the parent
          shows the request overview; clicking an iteration shows its detail.
          Mirrors the ID-role "View design request" layout. */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-start">
        <aside className="w-full lg:w-80 flex-shrink-0 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden lg:sticky lg:top-4">
          <div className="p-4 space-y-6 max-h-[75vh] overflow-y-auto">
            {/* Parent — the base design request */}
            <div>
              <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-2 px-1">
                Design request
              </h4>
              <button
                type="button"
                onClick={() => {
                  setInboxView("design_submission");
                  selectHashTab("overview");
                }}
                className={`w-full text-left rounded-xl transition-all flex gap-3 p-3 border ${
                  activeHashTab === "overview"
                    ? "bg-flamingo-50/60 border-flamingo-200 shadow-sm"
                    : "border-transparent hover:bg-slate-50 hover:border-slate-200"
                }`}
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-flamingo-500 to-flamingo-600 flex items-center justify-center shrink-0 shadow-sm">
                  <FileText size={16} className="text-white" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className={`text-sm font-semibold truncate ${activeHashTab === "overview" ? "text-slate-900" : "text-slate-700"}`}>
                    {portfolioTitle || "Design request"}
                  </h4>
                  <p className={`text-xs mt-0.5 ${activeHashTab === "overview" ? "text-flamingo-700" : "text-slate-500"}`}>
                    Requirements &amp; references
                  </p>
                </div>
              </button>

              {/* Interior Execution now lives in its own top-level section
                  (/interior-execution), reached once the design is developer-approved. */}
            </div>

            {/* Children — iterations, latest at the top */}
            <div>
              <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-2 px-1">
                Iterations
                {iterationCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 bg-slate-100 text-slate-600 text-[10px] font-bold rounded-full align-middle">
                    {iterationCount}
                  </span>
                )}
              </h4>
              {requests.length > 0 ? (
                <div className="space-y-1">
                  {requests.map((request) => {
                    const iter = request.iteration || {};
                    const isSelected =
                      (activeHashTab === "feedback" || activeHashTab === "boq") &&
                      selectedIterationId === request.id;
                    const isFinalIteration = Boolean(iter.isFinal ?? iter.is_final);
                    const statusDisplay = getIterationStatusDisplay(iter);
                    const rowTitle =
                      iter?.iterationName?.trim?.() ||
                      iter?.iteration_name?.trim?.() ||
                      `Iteration #${request.iterationNumber}`;
                    const isLatest = latestIterationIds.has(request.id);
                    return (
                      <div
                        key={request.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelectIteration(request)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSelectIteration(request);
                          }
                        }}
                        className={`relative w-full text-left rounded-xl transition-all flex flex-col gap-1 p-3 pl-4 border cursor-pointer ${
                          isSelected
                            ? "bg-flamingo-50/60 border-flamingo-200 shadow-sm"
                            : "border-transparent hover:bg-slate-50 hover:border-slate-200"
                        }`}
                      >
                        {isSelected && (
                          <span
                            className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-gradient-to-b from-flamingo-400 to-flamingo-600"
                            aria-hidden
                          />
                        )}
                        <div className="flex items-center gap-2 min-w-0">
                          <h4 className={`text-sm font-semibold truncate ${isSelected ? "text-slate-900" : "text-slate-700"}`}>
                            {rowTitle}
                          </h4>
                          {isFinalIteration && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                              Final
                            </span>
                          )}
                          {isLatest && (
                            <span className="ml-auto text-[10px] font-semibold text-flamingo-600 shrink-0">
                              Latest
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                              STATUS_TONE_CLASSES[statusDisplay.tone] || STATUS_TONE_CLASSES.amber
                            }`}
                          >
                            {statusDisplay.label}
                          </span>
                          <span className={`text-[11px] ${isSelected ? "text-flamingo-700" : "text-slate-400"}`}>
                            {formatDate(request.createdDate)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 px-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/40">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-slate-200 mb-2 shadow-sm">
                    <Layers className="text-slate-400" size={16} />
                  </div>
                  <p className="text-sm text-slate-700 font-medium">No iterations yet</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    They appear once the designer submits a design.
                  </p>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Right column — detail for the selected node (parent or iteration) */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Iteration sub-nav (Design / BOQ) — only when an iteration is open. BOQ tab is
              offered only on the FINAL iteration (BOQ is entered/valid only there). */}
          {(activeHashTab === "feedback" || activeHashTab === "boq") && selectedIteration && (
            <div className="inline-flex gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              {[
                { id: "feedback", label: "Design & feedback" },
                ...((selectedIteration?.isFinal ?? selectedIteration?.is_final)
                  ? [{ id: "boq", label: "BOQ" }]
                  : []),
              ].map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => selectHashTab(id)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    activeHashTab === id
                      ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

      {/* ─── Overview tab ─────────────────────────────────────────────── */}
      {activeHashTab === "overview" && (
        <div className="space-y-6">
          {/* Stat tiles */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-900">At a glance</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Snapshot of the design request between {customerName} and {designerName}.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {overviewStatTiles.map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-xl border border-slate-100 bg-slate-50/40 p-4">
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                    <Icon size={13} className="text-slate-400" />
                    {label}
                  </div>
                  <div className="mt-1.5 text-xl sm:text-2xl font-semibold text-slate-900 tabular-nums">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Request meta + Property unit */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:col-span-2">
              <h2 className="text-lg font-semibold text-slate-900">Request details</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {portfolioTitle || "Design submission"}
              </p>

              <div className="mt-5 grid grid-cols-1 gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Buyer</p>
                  <p className="mt-1 text-sm text-slate-900">{customerName}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Designer</p>
                  <p className="mt-1 text-sm text-slate-900">{designerName}</p>
                  {selectedLead?.isAvailableOnSite && (
                    <div className="mt-1.5">
                      <AvailabilityTag isAvailableOnSite size="xs" hideWhenRemote />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Project</p>
                  <p className="mt-1 text-sm text-slate-900">{project.projectName || "—"}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Location</p>
                  <p className="mt-1 text-sm text-slate-900">
                    {address.city || address.state
                      ? `${address.city || ""}${address.city && address.state ? ", " : ""}${address.state || ""}`
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Lead created</p>
                  <p className="mt-1 text-sm text-slate-900">
                    {selectedLead?.createdDate ? formatDate(selectedLead.createdDate) : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Status</p>
                  <p className="mt-1 text-sm text-slate-900">{ackStatusLabel}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Unit</h2>
              <p className="mt-0.5 text-sm text-slate-500">Property the request is tied to.</p>

              <dl className="mt-5 space-y-3 border-t border-slate-100 pt-5 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Unit no</dt>
                  <dd className="font-medium text-slate-900">{unitNo}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Configuration</dt>
                  <dd className="font-medium text-slate-900">{configType}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Carpet area</dt>
                  <dd className="font-medium text-slate-900">{area}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-slate-500">Tower / floor</dt>
                  <dd className="font-medium text-slate-900 text-right">{towerAndFloor}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Requirements */}
          {selectedLead?.requirements && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Requirements</h2>
                  <p className="mt-0.5 text-sm text-slate-500">Buyer's brief shared with the designer.</p>
                </div>
                <OutlineButton
                  onClick={() => setShowRequirementsModal(true)}
                  className="!w-auto !px-3 !py-1.5 text-sm whitespace-nowrap"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Maximize2 size={14} />
                    Expand
                  </span>
                </OutlineButton>
              </div>
              <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50/40 overflow-hidden">
                <div className="requirements-content overflow-y-auto p-4 text-sm max-h-80 min-h-[80px]">
                  <style>{`
                    .requirements-content h1, .requirements-content h2 { font-size: 1.125rem; font-weight: 600; color: #0f172a; margin: 1rem 0 0.5rem; }
                    .requirements-content h3 { font-size: 1rem; font-weight: 600; color: #0f172a; margin: 0.75rem 0 0.5rem; }
                    .requirements-content p { margin: 0.5rem 0; color: #334155; line-height: 1.6; font-size: 0.875rem; }
                    .requirements-content ul { margin: 0.5rem 0; padding-left: 1.5rem; color: #334155; }
                    .requirements-content li { margin: 0.25rem 0; line-height: 1.5; font-size: 0.875rem; }
                    .requirements-content strong { font-weight: 600; color: #0f172a; }
                    .requirements-content u { text-decoration: underline; }
                    .requirements-content blockquote { border-left: 4px solid #e2e8f0; padding-left: 1rem; margin: 0.5rem 0; color: #64748b; font-style: italic; }
                  `}</style>
                  <div dangerouslySetInnerHTML={{ __html: selectedLead.requirements }} />
                </div>
              </div>
            </div>
          )}

          {/* Status timeline */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900">Status timeline</h2>
            <p className="mt-0.5 text-sm text-slate-500">Lifecycle history of this design request.</p>
            <div className="mt-5 border-t border-slate-100 pt-5">
              <LeadStatusTimeline lead={selectedLead} />
            </div>
          </div>

          {/* Config images + Portfolio carousels */}
          {(configImages.length > 0 || designReferenceImages.length > 0) && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Config images */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                <h2 className="text-lg font-semibold text-slate-900">Config images</h2>
                <p className="mt-0.5 text-sm text-slate-500">Reference images of the unit configuration.</p>
                {configImages.length > 0 ? (
                  <div className="mt-5 relative flex justify-center items-center p-4">
                    {configImages.length > 1 && (
                      <div className="absolute transform -translate-x-24 scale-90 opacity-20 z-0 pointer-events-none">
                        <LoadingImage
                          src={getImageUrl(configImages[(configCarouselIndex - 1 + configImages.length) % configImages.length])}
                          alt="Previous"
                          containerClassName="w-48 h-48 rounded-xl"
                          className="w-48 h-48 object-cover rounded-xl"
                        />
                      </div>
                    )}
                    <div className="relative z-10">
                      {(() => {
                        const media = configImages[configCarouselIndex];
                        const imageSrc = getImageUrl(media);
                        return imageSrc ? (
                          <a href={imageSrc} target="_blank" rel="noopener noreferrer" className="block">
                            <LoadingImage
                              src={imageSrc}
                              alt={media?.name || `Config ${configCarouselIndex + 1}`}
                              containerClassName="w-full max-w-[280px] aspect-square rounded-xl shadow-lg mx-auto"
                              className="w-full max-w-[280px] aspect-square object-cover rounded-xl shadow-lg mx-auto"
                            />
                          </a>
                        ) : (
                          <div className="w-48 h-48 flex flex-col items-center justify-center rounded-xl bg-slate-50 border border-slate-200">
                            <FileText className="text-slate-400" size={24} />
                            <p className="text-sm text-slate-600 truncate">{media?.name}</p>
                          </div>
                        );
                      })()}
                    </div>
                    {configImages.length > 1 && (
                      <>
                        <div className="absolute transform translate-x-24 scale-90 opacity-20 z-0 pointer-events-none">
                          <LoadingImage
                            src={getImageUrl(configImages[(configCarouselIndex + 1) % configImages.length])}
                            alt="Next"
                            containerClassName="w-48 h-48 rounded-xl"
                            className="w-48 h-48 object-cover rounded-xl"
                          />
                        </div>
                        <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-1 z-20">
                          <button
                            type="button"
                            onClick={() => setConfigCarouselIndex((i) => (i > 0 ? i - 1 : configImages.length - 1))}
                            className="bg-white/95 hover:bg-white rounded-full p-2 shadow-lg text-slate-600 hover:text-slate-900"
                            aria-label="Previous"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfigCarouselIndex((i) => (i < configImages.length - 1 ? i + 1 : 0))}
                            className="bg-white/95 hover:bg-white rounded-full p-2 shadow-lg text-slate-600 hover:text-slate-900"
                            aria-label="Next"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                      </>
                    )}
                    {configImages.length > 1 && (
                      <p className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-xs text-slate-500 tabular-nums">
                        {configCarouselIndex + 1} / {configImages.length}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mt-5 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
                    <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                      <FileText className="text-slate-400" size={18} />
                    </div>
                    <p className="mt-2 text-sm text-slate-500">No config images</p>
                  </div>
                )}
              </div>

              {/* Portfolio / featured design */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                <h2 className="text-lg font-semibold text-slate-900">{designReferenceLabel}</h2>
                <p className="mt-0.5 text-sm text-slate-500">
                  {isFeaturedRequest ? "Featured design referenced for this request." : "Designer's shortlisted portfolio."}
                </p>
                {(() => {
                  if (designReferenceImages.length === 0) {
                    return (
                      <div className="mt-5 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
                        <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                          <ImageIcon className="text-slate-400" size={18} />
                        </div>
                        <p className="mt-2 text-sm text-slate-500">
                          {isFeaturedRequest ? "No design items" : "No portfolio images"}
                        </p>
                      </div>
                    );
                  }
                  const current = designReferenceImages[portfolioCarouselIndex];
                  const src = getImageUrl(current?.img);
                  return (
                    <div className="mt-5 relative flex justify-center items-center p-4">
                      {designReferenceImages.length > 1 && (
                        <div className="absolute transform -translate-x-24 scale-90 opacity-20 z-0 pointer-events-none">
                          <LoadingImage
                            src={getImageUrl(designReferenceImages[(portfolioCarouselIndex - 1 + designReferenceImages.length) % designReferenceImages.length]?.img)}
                            alt="Previous"
                            containerClassName="w-48 h-48 rounded-xl"
                            className="w-48 h-48 object-cover rounded-xl"
                          />
                        </div>
                      )}
                      <div className="relative z-10">
                        {src ? (
                          <a href={src} target="_blank" rel="noopener noreferrer" className="block">
                            <LoadingImage
                              src={src}
                              alt={`${current?.title || (isFeaturedRequest ? "Design" : "Portfolio")} ${(current?.idx || 0) + 1}`}
                              containerClassName="w-full max-w-[280px] aspect-square rounded-xl shadow-lg mx-auto"
                              className="w-full max-w-[280px] aspect-square object-cover rounded-xl shadow-lg mx-auto"
                            />
                          </a>
                        ) : (
                          <div className="w-48 h-48 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200">
                            <ImageIcon className="text-slate-400" size={24} />
                          </div>
                        )}
                      </div>
                      {designReferenceImages.length > 1 && (
                        <>
                          <div className="absolute transform translate-x-24 scale-90 opacity-20 z-0 pointer-events-none">
                            <LoadingImage
                              src={getImageUrl(designReferenceImages[(portfolioCarouselIndex + 1) % designReferenceImages.length]?.img)}
                              alt="Next"
                              containerClassName="w-48 h-48 rounded-xl"
                              className="w-48 h-48 object-cover rounded-xl"
                            />
                          </div>
                          <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-1 z-20">
                            <button
                              type="button"
                              onClick={() => setPortfolioCarouselIndex((i) => (i > 0 ? i - 1 : designReferenceImages.length - 1))}
                              className="bg-white/95 hover:bg-white rounded-full p-2 shadow-lg text-slate-600 hover:text-slate-900"
                              aria-label="Previous"
                            >
                              <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setPortfolioCarouselIndex((i) => (i < designReferenceImages.length - 1 ? i + 1 : 0))}
                              className="bg-white/95 hover:bg-white rounded-full p-2 shadow-lg text-slate-600 hover:text-slate-900"
                              aria-label="Next"
                            >
                              <ChevronRight className="w-5 h-5" />
                            </button>
                          </div>
                          <p className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-xs text-slate-500 tabular-nums">
                            {portfolioCarouselIndex + 1} / {designReferenceImages.length}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Iterations tab ────────────────────────────────────────────── */}
      {activeHashTab === "iterations" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Iterations</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Every design iteration shared by {designerName}. Click any card to review its feedback.
              </p>
            </div>
            {iterationCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <Layers size={12} />
                {iterationCount} {iterationCount === 1 ? "iteration" : "iterations"}
              </span>
            )}
          </div>

          {loadingDesigns ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 animate-pulse rounded-2xl bg-slate-100" />
              ))}
            </div>
          ) : requests.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Layers className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">No iterations yet</p>
              <p className="mt-1 text-sm text-slate-500">
                Iterations will appear here once the designer submits a design.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {requests.map((request) => {
                const iter = request.iteration || {};
                const isFinalIteration = Boolean(iter.isFinal ?? iter.is_final);
                const statusDisplay = getIterationStatusDisplay(iter);
                // "New" only applies to a fresh, undecided iteration awaiting
                // the first buyer action.
                const isUndecided = statusDisplay.key === "AWAITING";
                const isSelectedHere =
                  inboxView === "interactions" && selectedIterationId === request.id;
                const iterationTitle =
                  iter?.interactionName?.trim?.() ||
                  iter?.interaction_name?.trim?.() ||
                  iter?.iterationName?.trim?.() ||
                  iter?.iteration_name?.trim?.() ||
                  iter?.title?.trim?.() ||
                  request.title ||
                  `Iteration #${request.iterationNumber}`;

                // First image for the thumbnail
                let thumbSrc = null;
                if (iter.media) {
                  const bySection = normalizeMediaBySection(iter.media);
                  const firstItem = Object.values(bySection || {}).flat()[0];
                  if (firstItem) thumbSrc = getImageSrc(firstItem);
                }

                return (
                  <button
                    type="button"
                    key={request.id}
                    onClick={() => handleSelectIteration(request)}
                    className={`group flex flex-col overflow-hidden rounded-2xl border bg-white text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                      isSelectedHere ? "border-flamingo-400 ring-1 ring-flamingo-200" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="relative h-40 w-full overflow-hidden bg-slate-50">
                      {thumbSrc ? (
                        <LoadingImage
                          src={thumbSrc}
                          alt={iterationTitle}
                          containerClassName="h-40 w-full"
                          className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <ImageIcon className="text-slate-300" size={28} />
                        </div>
                      )}
                      <div className="absolute top-2 right-2 flex flex-wrap items-center gap-1">
                        {isFinalIteration && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/95 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm backdrop-blur-sm">
                            <CheckCircle2 size={10} />
                            Final
                          </span>
                        )}
                        {isUndecided && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-flamingo-500/95 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm backdrop-blur-sm">
                            New
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 p-4">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {iterationTitle}
                        </p>
                        <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 tabular-nums">
                          #{request.iterationNumber}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Calendar size={11} />
                        {formatDate(request.createdDate)}
                      </p>
                      {iter.estimatedPrice ? (
                        <p className="text-sm font-semibold text-flamingo-600">
                          ₹{parseFloat(iter.estimatedPrice).toLocaleString("en-IN")}
                        </p>
                      ) : null}
                      <div className="mt-1 flex items-center gap-1.5">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                            STATUS_TONE_CLASSES[statusDisplay.tone] || STATUS_TONE_CLASSES.amber
                          }`}
                        >
                          {statusDisplay.tone === "amber" ? <Clock size={10} /> : <CheckCircle2 size={10} />}
                          {statusDisplay.label}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── Feedback tab ──────────────────────────────────────────────── */}
      {activeHashTab === "feedback" && (
        <div className="space-y-6">
          {/* Simplified design conversation — one thread + two clear reviewer actions. */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-900">Design conversation</h2>
              {conversation?.status && <DesignPhaseBadge phase={conversation.status} />}
            </div>
            {loadingConversation && !conversation ? (
              <p className="text-sm text-slate-400">Loading conversation…</p>
            ) : (
              <DesignConversation
                deliverables={conversation?.deliverables || []}
                perspective="BV"
                onOpenMedia={() => selectHashTab("iterations")}
              />
            )}
            {(() => {
              const conv = conversation;
              if (!conv || conv.status !== "IN_REVIEW") return null;
              const dels = conv.deliverables || [];
              const latestIsFinal = Boolean(dels.length && dels[dels.length - 1].isFinal);
              return (
                latestIsFinal ? (
                  <div className="mt-5 border-t border-slate-100 pt-4">
                    <div className="flex flex-wrap gap-3">
                      {/* Approve only. "Request changes" lives in the iteration detail's
                          "Request modification" action (single canonical change-request flow). */}
                      <PrimaryButton
                        disabled={submittingAction}
                        onClick={handleApproveDesign}
                        className="!w-auto !px-4 !py-2 text-sm"
                      >
                        Approve design
                      </PrimaryButton>
                    </div>
                  </div>
                ) : null
              );
            })()}
          </div>
          {!selectedIteration || !selectedSubmission ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <MessageSquare className="text-slate-400" size={22} />
                </div>
                <p className="mt-3 text-base font-medium text-slate-800">No iteration selected</p>
                <p className="mt-1 text-sm text-slate-500">
                  {requests.length > 0
                    ? "Open an iteration from the Iterations tab to review its feedback."
                    : "Iterations appear after the designer submits a design."}
                </p>
                {requests.length > 0 && (
                  <OutlineButton
                    onClick={() => selectHashTab("iterations")}
                    className="mt-4 !w-auto !px-3 !py-1.5 text-sm"
                  >
                    Browse iterations
                  </OutlineButton>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Iteration header card */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-semibold text-slate-900">
                        {selectedIterationName || selectedSubmission.title || "Design submission"}
                      </h2>
                      {Boolean(selectedIteration?.isFinal ?? selectedIteration?.is_final) && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                          <CheckCircle2 size={11} />
                          Final
                        </span>
                      )}
                      {/* Decision pill — shows when buyer has already
                          approved or rejected this iteration. */}
                      {(() => {
                        const itStatus = (selectedIteration?.iterationStatus ?? selectedIteration?.iteration_status ?? '').toString().toUpperCase();
                        if (itStatus === 'APPROVED') {
                          return (
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                              <CheckCircle2 size={11} />
                              Approved
                            </span>
                          );
                        }
                        if (itStatus === 'REJECTED') {
                          return (
                            <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
                              Rejected
                            </span>
                          );
                        }
                        return null;
                      })()}
                      {/* Developer-approval pill — independent of the buyer
                          decision. This is what reflects the developer's
                          approve/reject done from the Approvals screen. */}
                      {(() => {
                        const itStatus = (selectedIteration?.iterationStatus ?? selectedIteration?.iteration_status ?? '').toString().toUpperCase();
                        const devStatus = (selectedIteration?.developerApprovalStatus ?? selectedIteration?.developer_approval_status ?? '').toString().toUpperCase();
                        if (devStatus === 'DEVELOPER_APPROVED') {
                          return (
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                              <CheckCircle2 size={11} />
                              Developer approved
                            </span>
                          );
                        }
                        if (devStatus === 'DEVELOPER_REJECTED') {
                          return (
                            <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
                              Developer rejected
                            </span>
                          );
                        }
                        // Only worth showing "pending" once the buyer has approved
                        // (before that, developer approval isn't in play yet).
                        if (itStatus === 'APPROVED' && devStatus === 'DEVELOPER_PENDING') {
                          return (
                            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                              <Clock size={11} />
                              Developer pending
                            </span>
                          );
                        }
                        return null;
                      })()}
                      {selectedIteration.estimatedPrice && (
                        <span className="text-base font-semibold text-flamingo-600">
                          ₹{parseFloat(selectedIteration.estimatedPrice).toLocaleString("en-IN")}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      Iteration #{selectedIteration.iterationNumber || 1}
                      {selectedIteration.createdDate ? ` · ${formatDate(selectedIteration.createdDate)}` : ""}
                    </p>
                  </div>
                  {(() => {
                    const itStatus = (selectedIteration?.iterationStatus ?? selectedIteration?.iteration_status ?? '').toString().toUpperCase();
                    const decided = itStatus === 'APPROVED' || itStatus === 'REJECTED';
                    if (!isSelectedIterationLatest) {
                      // A newer iteration exists — this one is frozen.
                      return (
                        <span className="text-xs text-slate-500 italic">
                          Superseded by a newer iteration.
                        </span>
                      );
                    }
                    if (decided) {
                      // Final state — no further actions.
                      return (
                        <span className="text-xs text-slate-500 italic">
                          Decision recorded.
                        </span>
                      );
                    }
                    return (
                      <div className="flex flex-wrap items-center gap-2">
                        <OutlineButton
                          onClick={() =>
                            setPendingDecision({
                              type: 'REJECT',
                              iterationId: selectedIterationId,
                              iteration: selectedIteration,
                            })
                          }
                          className="!w-auto !px-3 !py-1.5 text-sm whitespace-nowrap !border-rose-200 !text-rose-700 hover:!bg-rose-50"
                          disabled={decidingIteration || sendingModification}
                          title="Reject this design"
                        >
                          Reject
                        </OutlineButton>
                        <OutlineButton
                          onClick={() =>
                            openRequestModification({
                              id: selectedIterationId,
                              iteration: selectedIteration,
                              submission: selectedSubmission,
                            })
                          }
                          className="!w-auto !px-3 !py-1.5 text-sm whitespace-nowrap"
                          disabled={hasBuyerFeedbackForSelectedIteration || decidingIteration}
                          title={
                            hasBuyerFeedbackForSelectedIteration
                              ? "You have already sent feedback for this iteration"
                              : "Request a modification for this iteration"
                          }
                        >
                          <span className="inline-flex items-center gap-1.5">
                            <Send size={13} />
                            Request modification
                          </span>
                        </OutlineButton>
                        {/* Approve is only valid on a FINAL design — a sample
                            iteration can be given feedback or rejected, but not
                            approved as the final design. */}
                        {Boolean(selectedIteration?.isFinal ?? selectedIteration?.is_final) && (
                          <PrimaryButton
                            onClick={() =>
                              setPendingDecision({
                                type: 'APPROVE',
                                iterationId: selectedIterationId,
                                iteration: selectedIteration,
                              })
                            }
                            className="!w-auto !px-3 !py-1.5 text-sm whitespace-nowrap"
                            disabled={decidingIteration || sendingModification}
                            title="Approve this design"
                          >
                            <span className="inline-flex items-center gap-1.5">
                              <CheckCircle2 size={13} />
                              Approve
                            </span>
                          </PrimaryButton>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Property quick row */}
                <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 border-t border-slate-100 pt-4 text-sm">
                  {[
                    unitNo !== "N/A" && { label: "Unit", value: unitNo },
                    configType !== "N/A" && { label: "Type", value: configType },
                    area !== "N/A" && { label: "Area", value: area },
                    towerAndFloor !== "N/A" && { label: "Location", value: towerAndFloor },
                    selectedSubmission.createdDate && {
                      label: "Submitted",
                      value: formatDate(selectedSubmission.createdDate),
                    },
                  ]
                    .filter(Boolean)
                    .map((item, idx) => (
                      <div key={idx}>
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          {item.label}
                        </p>
                        <p className="mt-0.5 text-sm text-slate-900 truncate max-w-[200px]">{item.value}</p>
                      </div>
                    ))}
                </div>
              </div>

              {/* Design images carousel + feedback thread */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Design images */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:col-span-2">
                  <h2 className="text-lg font-semibold text-slate-900">Design images</h2>
                  <p className="mt-0.5 text-sm text-slate-500">Designs shared in this iteration.</p>
                  {selectedIteration.media ? (
                    (() => {
                      const bySection = normalizeMediaBySection(selectedIteration.media);
                      const sectionOrder = getSectionDisplayOrder();
                      const sectionsWithMedia = sectionOrder.filter((sec) => bySection[sec]?.length > 0);
                      const uncategorizedKeys = Object.keys(bySection).filter((k) => !sectionOrder.includes(k));
                      const allSectionKeys = [...sectionsWithMedia, ...uncategorizedKeys];
                      const allItems = allSectionKeys.flatMap((sec) =>
                        (bySection[sec] || []).map((item) =>
                          typeof item === "string" ? { key: item, section: sec } : { ...item, section: sec }
                        )
                      );
                      if (allItems.length === 0) {
                        return (
                          <div className="mt-5 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
                            <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                              <ImageIcon className="text-slate-400" size={18} />
                            </div>
                            <p className="mt-2 text-sm text-slate-500">No images</p>
                          </div>
                        );
                      }
                      const current = allItems[designCarouselIndex];
                      const imageSrc = getImageSrc(current);
                      return (
                        <div className="mt-5 relative flex justify-center items-center p-4">
                          {allItems.length > 1 && (
                            <>
                              <div className="absolute -translate-x-16 lg:-translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                <LoadingImage
                                  src={getImageSrc(allItems[(designCarouselIndex - 1 + allItems.length) % allItems.length])}
                                  alt="Previous"
                                  containerClassName="w-32 h-32 lg:w-40 lg:h-40 rounded-xl"
                                  className="w-32 h-32 lg:w-40 lg:h-40 object-cover rounded-xl"
                                />
                              </div>
                              <div className="absolute translate-x-16 lg:translate-x-20 scale-90 opacity-20 z-0 pointer-events-none">
                                <LoadingImage
                                  src={getImageSrc(allItems[(designCarouselIndex + 1) % allItems.length])}
                                  alt="Next"
                                  containerClassName="w-32 h-32 lg:w-40 lg:h-40 rounded-xl"
                                  className="w-32 h-32 lg:w-40 lg:h-40 object-cover rounded-xl"
                                />
                              </div>
                            </>
                          )}
                          <div className="relative z-10">
                            {imageSrc ? (
                              <a
                                href={imageSrc}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block relative w-full max-w-[220px] lg:max-w-[300px] aspect-square mx-auto rounded-xl overflow-hidden shadow-lg"
                              >
                                <LoadingImage
                                  src={imageSrc}
                                  alt={current?.section ? `${current.section} ${designCarouselIndex + 1}` : `Design ${designCarouselIndex + 1}`}
                                  containerClassName="w-full h-full rounded-xl"
                                  className="w-full h-full object-cover"
                                />
                              </a>
                            ) : (
                              <div className="w-40 h-40 flex flex-col items-center justify-center rounded-xl bg-slate-50 border border-slate-200">
                                <FileText className="text-slate-400" size={24} />
                                <p className="text-sm text-slate-600 mt-1">{current?.section || "Design"}</p>
                              </div>
                            )}
                          </div>
                          {allItems.length > 1 && (
                            <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-1 z-20">
                              <button
                                type="button"
                                onClick={() => setDesignCarouselIndex((i) => (i > 0 ? i - 1 : allItems.length - 1))}
                                className="bg-white/95 hover:bg-white rounded-full p-1.5 shadow-lg text-slate-600 hover:text-slate-900"
                                aria-label="Previous"
                              >
                                <ChevronLeft className="w-5 h-5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDesignCarouselIndex((i) => (i < allItems.length - 1 ? i + 1 : 0))}
                                className="bg-white/95 hover:bg-white rounded-full p-1.5 shadow-lg text-slate-600 hover:text-slate-900"
                                aria-label="Next"
                              >
                                <ChevronRight className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                          {allItems.length > 1 && (
                            <p className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-xs text-slate-500 tabular-nums">
                              {designCarouselIndex + 1} / {allItems.length}
                              {current?.section && (
                                <span className="ml-1.5 text-slate-400">· {current.section}</span>
                              )}
                            </p>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="mt-5 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
                      <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                        <ImageIcon className="text-slate-400" size={18} />
                      </div>
                      <p className="mt-2 text-sm text-slate-500">No images for this iteration</p>
                    </div>
                  )}
                </div>

                {/* Feedback thread */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                  <h2 className="text-lg font-semibold text-slate-900">Conversation</h2>
                  <p className="mt-0.5 text-sm text-slate-500">Designer notes &amp; buyer feedback for this iteration.</p>

                  <div className="mt-5 space-y-3">
                    {/* Designer notes bubble (left, slate) */}
                    {(selectedIteration.designerNotes || selectedSubmission.description) && (
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 ring-1 ring-slate-200">
                          <Palette size={13} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                            {selectedIteration.designerNotes ? "Designer notes" : "Description"}
                          </p>
                          <div
                            className="mt-1 rounded-2xl rounded-tl-sm bg-slate-50 p-3 text-sm text-slate-700 leading-relaxed cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() =>
                              openNotesModal(
                                selectedIteration.designerNotes || selectedSubmission.description,
                                selectedIteration.designerNotes ? "Designer notes" : "Description"
                              )
                            }
                            title="Click to view full comment"
                          >
                            <p className="line-clamp-5 whitespace-pre-wrap">
                              {selectedIteration.designerNotes || selectedSubmission.description}
                            </p>
                          </div>
                          {selectedIteration.createdDate && (
                            <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                              <Clock size={10} />
                              {formatDate(selectedIteration.createdDate)}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Buyer feedback bubble (right, flamingo) */}
                    {selectedIteration.buyerFeedback && (
                      <div className="flex items-start gap-2 flex-row-reverse">
                        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 ring-1 ring-flamingo-200">
                          <User size={13} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium uppercase tracking-wide text-flamingo-600 text-right">
                            Buyer feedback
                          </p>
                          <div className="mt-1 rounded-2xl rounded-tr-sm bg-flamingo-50 p-3 text-sm text-slate-800 leading-relaxed border border-flamingo-100">
                            <p className="whitespace-pre-wrap">{selectedIteration.buyerFeedback}</p>
                          </div>
                          {selectedIteration.feedbackDate && (
                            <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400 justify-end">
                              <Clock size={10} />
                              {formatDateTime(selectedIteration.feedbackDate)}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Buyer feedback media */}
                    {(() => {
                      const feedbackMedia =
                        selectedIteration?.buyerFeedbackMedia ?? selectedIteration?.buyer_feedback_media;
                      const mediaList = Array.isArray(feedbackMedia) ? feedbackMedia : [];
                      if (mediaList.length === 0) return null;
                      return (
                        <div className="flex items-start gap-2 flex-row-reverse">
                          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-flamingo-100 text-flamingo-600 ring-1 ring-flamingo-200">
                            <ImageIcon size={13} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-flamingo-600 text-right">
                              Feedback images
                            </p>
                            <div className="mt-1 flex flex-wrap gap-2 justify-end">
                              {mediaList.map((item, idx) => {
                                const url = item?.url ?? item?.downloadUrl;
                                const caption = item?.caption ?? "";
                                if (!url) return null;
                                return (
                                  <div key={idx} className="flex flex-col items-end">
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block rounded-xl overflow-hidden border border-flamingo-100 hover:border-flamingo-300 w-20 h-20"
                                    >
                                      <LoadingImage
                                        src={url}
                                        alt={caption || `Feedback ${idx + 1}`}
                                        containerClassName="w-full h-full"
                                        className="w-full h-full object-cover"
                                      />
                                    </a>
                                    {caption && (
                                      <p className="text-[10px] text-slate-500 mt-0.5 max-w-20 truncate">
                                        {caption}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {!selectedIteration.buyerFeedback &&
                      !(
                        Array.isArray(selectedIteration?.buyerFeedbackMedia) &&
                        selectedIteration.buyerFeedbackMedia.length > 0
                      ) &&
                      !(
                        Array.isArray(selectedIteration?.buyer_feedback_media) &&
                        selectedIteration.buyer_feedback_media.length > 0
                      ) &&
                      !selectedIteration.designerNotes &&
                      !selectedSubmission?.description && (
                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-8 text-center">
                          <p className="text-sm text-slate-400">No activity yet</p>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── BOQ tab ───────────────────────────────────────────────────── */}
      {activeHashTab === "boq" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Bill of Quantities</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                BOQ for the currently selected iteration.
              </p>
            </div>
            {selectedIteration && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <Receipt size={12} />
                Iteration #{selectedIteration.iterationNumber || 1}
              </span>
            )}
          </div>
          {!selectedIteration || !selectedSubmission ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Receipt className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">No iteration selected</p>
              <p className="mt-1 text-sm text-slate-500">
                Open an iteration to view its BOQ.
              </p>
              {requests.length > 0 && (
                <OutlineButton
                  onClick={() => selectHashTab("iterations")}
                  className="mt-4 !w-auto !px-3 !py-1.5 text-sm"
                >
                  Browse iterations
                </OutlineButton>
              )}
            </div>
          ) : !(selectedIteration?.isFinal ?? selectedIteration?.is_final) ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Receipt className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">BOQ not available yet</p>
              <p className="mt-1 text-sm text-slate-500">
                The bill of quantities is entered only on the final design iteration. It will appear
                here once the designer marks an iteration as final.
              </p>
            </div>
          ) : (
            <BoqReadOnlyView
              designIterationId={
                selectedIteration?.designIterationId ||
                selectedIteration?.design_iteration_id ||
                selectedIteration?.id ||
                selectedIterationId
              }
              designSubmissionId={
                selectedSubmission?.designSubmissionId ||
                selectedSubmission?.design_submission_id ||
                selectedSubmission?.id
              }
              portfolioId={selectedLead?.interiorDesignerPortfolioItems?.[0]?.portfolioItemId}
            />
          )}
        </div>
      )}
        </div>{/* /right detail column */}
      </div>{/* /master–detail flex */}


      {Boolean(notesModalContent) && (
        <div
          className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
          onClick={() => setNotesModalContent("")}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-slate-100 shrink-0">
              <h3 className="text-lg font-semibold text-slate-900">{notesModalTitle}</h3>
              <CloseButton
                onClick={() => setNotesModalContent("")}
                aria-label="Close"
              >
                <X size={18} />
              </CloseButton>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                {notesModalContent}
              </p>
            </div>
          </div>
        </div>
      )}

      {showRequirementsModal && selectedLead?.requirements && (
        <div
          className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowRequirementsModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="text-slate-500" size={18} />
                <h3 className="text-lg font-semibold text-slate-900">Requirements</h3>
              </div>
              <CloseButton
                onClick={() => setShowRequirementsModal(false)}
                aria-label="Close"
              >
                <X size={18} />
              </CloseButton>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <style>{`
                .requirements-content h1, .requirements-content h2 { font-size: 1.125rem; font-weight: 600; color: #0f172a; margin: 1rem 0 0.5rem; }
                .requirements-content h3 { font-size: 1rem; font-weight: 600; color: #0f172a; margin: 0.75rem 0 0.5rem; }
                .requirements-content p { margin: 0.5rem 0; color: #334155; line-height: 1.6; font-size: 0.875rem; }
                .requirements-content ul { margin: 0.5rem 0; padding-left: 1.5rem; color: #334155; }
                .requirements-content li { margin: 0.25rem 0; line-height: 1.5; font-size: 0.875rem; }
                .requirements-content strong { font-weight: 600; color: #0f172a; }
                .requirements-content u { text-decoration: underline; }
                .requirements-content blockquote { border-left: 4px solid #e2e8f0; padding-left: 1rem; margin: 0.5rem 0; color: #64748b; font-style: italic; }
              `}</style>
              <div
                className="requirements-content"
                dangerouslySetInnerHTML={{ __html: selectedLead.requirements }}
              />
            </div>
          </div>
        </div>
      )}

        {/* Buyer decision modal — Approve / Reject confirmation */}
        {pendingDecision && (
          <div
            className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
            onClick={() => !decidingIteration && setPendingDecision(null)}
          >
            <div
              className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  {pendingDecision.type === 'APPROVE'
                    ? 'Approve this design?'
                    : 'Reject this design?'}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {pendingDecision.type === 'APPROVE'
                    ? 'The designer will be notified that you accept this iteration. Once approved, it moves to developer review.'
                    : 'The designer will be notified that you reject this iteration. You won\'t be able to undo this from this screen.'}
                </p>
                <div className="mt-6 flex items-center justify-end gap-2">
                  <OutlineButton
                    onClick={() => setPendingDecision(null)}
                    disabled={decidingIteration}
                    className="!w-auto !px-3 !py-1.5 text-sm"
                  >
                    Cancel
                  </OutlineButton>
                  {pendingDecision.type === 'APPROVE' ? (
                    <PrimaryButton
                      onClick={submitDecision}
                      disabled={decidingIteration}
                      className="!w-auto !px-3 !py-1.5 text-sm"
                    >
                      {decidingIteration ? 'Approving…' : 'Yes, approve'}
                    </PrimaryButton>
                  ) : (
                    <button
                      type="button"
                      onClick={submitDecision}
                      disabled={decidingIteration}
                      className="inline-flex items-center justify-center rounded-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-semibold px-3 py-1.5 text-sm"
                    >
                      {decidingIteration ? 'Rejecting…' : 'Yes, reject'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Request modification modal */}
        {showRequestModificationModal && (
          <div
            className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
            onClick={closeRequestModification}
          >
            <div
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 sm:p-7">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Request modification</h3>
                    <p className="mt-0.5 text-xs text-slate-500">Send feedback on this iteration to the designer.</p>
                  </div>
                  <CloseButton
                    onClick={closeRequestModification}
                    disabled={sendingModification || payingDesignAdvance || uploadingFeedbackMedia}
                    aria-label="Close"
                  />
                </div>

                <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
                  Comments
                </label>
                <textarea
                  value={modificationDescription}
                  onChange={(e) => setModificationDescription(e.target.value)}
                  placeholder="Add a feedback comment"
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-flamingo-400 focus:border-flamingo-400 text-sm text-slate-800 placeholder:text-slate-400"
                />

                <div className="mt-4">
                  <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
                    Feedback images (optional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFeedbackMediaSelect}
                    disabled={uploadingFeedbackMedia}
                    className="hidden"
                    id="feedback-media-input"
                  />
                  <label
                    htmlFor="feedback-media-input"
                    className={`flex items-center justify-center gap-2 w-full px-4 py-3 border border-dashed border-slate-300 rounded-xl cursor-pointer transition-colors ${uploadingFeedbackMedia ? "bg-slate-50 pointer-events-none" : "hover:border-flamingo-400 hover:bg-flamingo-50/40"}`}
                  >
                    {uploadingFeedbackMedia ? (
                      <>
                        <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
                        <span className="text-sm text-slate-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-slate-500" />
                        <span className="text-sm text-slate-600">Add images</span>
                      </>
                    )}
                  </label>
                  {modificationFeedbackMedia.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {modificationFeedbackMedia.map((item, index) => (
                        <div
                          key={index}
                          className="flex gap-3 items-start p-2 rounded-xl border border-slate-200 bg-slate-50/60"
                        >
                          <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-slate-200 bg-white">
                            <LoadingImage
                              src={item.url}
                              alt={`Feedback ${index + 1}`}
                              containerClassName="w-full h-full"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <input
                              type="text"
                              value={item.caption || ""}
                              onChange={(e) =>
                                updateFeedbackMediaCaption(index, e.target.value)
                              }
                              placeholder="Caption (optional)"
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-flamingo-400 focus:border-flamingo-400"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFeedbackMedia(index)}
                            className="flex-shrink-0 p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                            aria-label="Remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-3 mt-5 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={modificationIsFinal}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setModificationIsFinal(checked);
                      if (checked && !isDesignAdvancePaid) {
                        setShowDesignAdvanceModal(true);
                      }
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500"
                  />
                  Mark as final iteration
                </label>

                {modificationIsFinal && (
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">Design advance payment</p>
                        <p className="mt-1 text-sm text-slate-600">
                          Required before sending a request marked as <span className="font-semibold">Final</span>.
                        </p>
                        {isDesignAdvancePaid ? (
                          <p className="mt-2 text-sm text-emerald-700 font-medium">
                            Paid (₹{(leadPaymentsByLeadId?.[parseInt(currentLeadId || 0)]?.amount || 0).toLocaleString("en-IN")}) · Ref:{" "}
                            {leadPaymentsByLeadId?.[parseInt(currentLeadId || 0)]?.reference || "—"}
                          </p>
                        ) : (
                          <p className="mt-2 text-sm text-amber-700 font-medium">
                            Not paid yet
                          </p>
                        )}
                      </div>

                      <PrimaryButton
                        onClick={() => setShowDesignAdvanceModal(true)}
                        disabled={payingDesignAdvance || isDesignAdvancePaid}
                        className="!w-auto !px-3 !py-1.5 text-sm whitespace-nowrap"
                      >
                        {isDesignAdvancePaid ? "Payment done" : "Pay now"}
                      </PrimaryButton>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 mt-8">
                  <OutlineButton
                    onClick={closeRequestModification}
                    disabled={sendingModification || payingDesignAdvance || uploadingFeedbackMedia}
                    className="!w-auto !px-4 !py-2 text-sm"
                  >
                    Cancel
                  </OutlineButton>
                  <PrimaryButton
                    onClick={submitRequestModification}
                    disabled={sendingModification || payingDesignAdvance || uploadingFeedbackMedia || (modificationIsFinal && !isDesignAdvancePaid)}
                    className="!w-auto !px-4 !py-2 text-sm"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Send size={13} />
                      {sendingModification ? "Sending..." : uploadingFeedbackMedia ? "Uploading..." : "Send request"}
                    </span>
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Design advance payment modal */}
        {showRequestModificationModal && showDesignAdvanceModal && (
          <div
            className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[60] p-4"
            onClick={() => {
              if (!payingDesignAdvance) setShowDesignAdvanceModal(false);
            }}
          >
            <div
              className="relative bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 sm:p-7">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Design advance payment</h3>
                    <p className="mt-0.5 text-sm text-slate-500">
                      Required before sending a request marked as Final.
                    </p>
                  </div>
                  <CloseButton
                    onClick={() => setShowDesignAdvanceModal(false)}
                    disabled={payingDesignAdvance}
                    aria-label="Close"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
                      Payment amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={designAdvanceAmount}
                      onChange={(e) => setDesignAdvanceAmount(e.target.value)}
                      disabled={payingDesignAdvance}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-flamingo-400 focus:border-flamingo-400 disabled:opacity-50 text-sm text-slate-800"
                      placeholder="5000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
                      Payment reference
                    </label>
                    <input
                      type="text"
                      value={designAdvanceReference}
                      onChange={(e) => setDesignAdvanceReference(e.target.value)}
                      disabled={payingDesignAdvance}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-flamingo-400 focus:border-flamingo-400 disabled:opacity-50 text-sm text-slate-800"
                      placeholder="razorpay_payment_id_or_ref"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Use your gateway reference (e.g. Razorpay payment id).
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-8">
                  <OutlineButton
                    onClick={() => setShowDesignAdvanceModal(false)}
                    disabled={payingDesignAdvance}
                    className="!w-auto !px-4 !py-2 text-sm"
                  >
                    Cancel
                  </OutlineButton>
                  <PrimaryButton
                    onClick={async () => {
                      const ok = await submitDesignAdvancePayment();
                      if (ok) {
                        setShowDesignAdvanceModal(false);
                      }
                    }}
                    disabled={payingDesignAdvance}
                    className="!w-auto !px-4 !py-2 text-sm"
                  >
                    {payingDesignAdvance ? "Processing..." : "Pay & continue"}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default RequestDetails;
